import React from "react";
import moment from "moment";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsSelect from "../clients/Tools/kit/ToolsSelect";
import PlaybookLibraryService from "../../services/PlaybookLibraryService";
import SkillLibraryService from "../../services/SkillLibraryService";
import ToolLibraryService from "../../services/ToolLibraryService";
import UtilityService from "../../services/UtilityService";
import AgentSkillTools from "./AgentSkillTools";
import BuildDrafts from "./BuildDrafts";
import PlaybookCategoryCard from "./PlaybookCategoryCard";
import PlaybookCategoryView from "./PlaybookCategoryView";

import "./agent-skill-list.css";

class AgentSkillList extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			skills: [],
			catalogue: [],
			isLoading: true,
			loadError: null,
			search: "",
			kind: "all",
			toolName: "",
		};
	}

	componentDidMount() {
		this.fetchSkills();
	}

	fetchSkills = async () => {
		try {
			const [skills, catalogue] = await Promise.all([SkillLibraryService.listSkills(), ToolLibraryService.listToolCatalogue()]);
			await this.update({ skills, catalogue, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: UtilityService.describeRequestError({ error, fallback: "The skills could not be loaded." }) });
		}
	};

	onOpenCategory = ({ categoryId }) => {
		this.update({ search: "", kind: "all", toolName: "" });
		this.props.onSelectCategory({ categoryId });
	};

	onBackToCategories = () => {
		this.props.onSelectCategory({ categoryId: null });
	};

	onClearNarrowing = () => {
		this.update({ search: "", kind: "all", toolName: "" });
	};

	onRowKeyDown = ({ event, skill }) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		this.props.onOpenSkill({ skillId: skill.id });
	};

	renderSkill = skill => {
		const { onOpenSkill } = this.props;
		const { catalogue } = this.state;
		const authorNote = skill.isBuiltin ? "SRJ" : skill.createdBy;

		return (
			<div
				key={skill.id}
				role="button"
				tabIndex={0}
				className="tk-table__row tk-table__row--body tk-table__row--clickable agent-skill-list__row"
				onClick={() => onOpenSkill({ skillId: skill.id })}
				onKeyDown={event => this.onRowKeyDown({ event, skill })}
			>
				<span className="tk-table__lead">
					<span className="tk-icon-tile tk-icon-tile--sm">
						<Icon.BookOpen />
					</span>
					<span className="tk-table__stack">
						<span className="agent-skill-list__name">
							<span className="tk-table__primary tk-table__truncate">{skill.name}</span>
							<span className={clsx("tk-badge", skill.isBuiltin ? "tk-badge--accent" : "tk-badge--neutral")}>
								{skill.isBuiltin ? "Built-in" : "Custom"}
							</span>
						</span>
						<span className="tk-table__meta tk-table__truncate">by {authorNote}</span>
					</span>
				</span>
				<span className="agent-skill-list__description" title={skill.description}>
					{skill.description}
				</span>
				<AgentSkillTools toolNames={skill.toolNames} catalogue={catalogue} mostShown={3} />
				<span className="tk-table__numeric">v{skill.versionNumber}</span>
				<span className="tk-table__numeric">{skill.usedByCount || 0}</span>
				<span className="tk-table__stack tk-table__cell--end">
					<span>{moment(skill.updatedAt).fromNow()}</span>
				</span>
			</div>
		);
	};

	renderTable({ shownSkills }) {
		return (
			<div className="tk-table tk-card">
				<div className="tk-table__row tk-table__row--head agent-skill-list__row">
					<span className="tk-table__head-cell">Skill</span>
					<span className="tk-table__head-cell">What it is for</span>
					<span className="tk-table__head-cell">Tools it uses</span>
					<span className="tk-table__head-cell">Version</span>
					<span className="tk-table__head-cell">Playbooks</span>
					<span className="tk-table__head-cell tk-table__cell--end">Last saved</span>
				</div>
				{shownSkills.map(this.renderSkill)}
			</div>
		);
	}

	renderCategories() {
		const { categories } = this.props;
		const ordered = PlaybookLibraryService.orderCategoriesForLibrary({ categories, countKey: "skillCount" });

		return (
			<div className="playbook-category-grid">
				{ordered.map(category => (
					<PlaybookCategoryCard
						key={category.id}
						category={category}
						isEmpty={category.skillCount === 0}
						countText={
							category.skillCount === 0 ? "No skills yet" : UtilityService.describeCount({ count: category.skillCount, singular: "skill" })
						}
						onClick={() => this.onOpenCategory({ categoryId: category.id })}
					/>
				))}
			</div>
		);
	}

	renderCategory({ category }) {
		const { onNewSkill, isStartingSkill } = this.props;
		const { skills, catalogue, search, kind, toolName } = this.state;
		const categorySkills = SkillLibraryService.skillsInCategory({ skills, categoryId: category.id });
		const shownSkills = SkillLibraryService.skillsMatching({ skills: categorySkills, search, kind, toolName });
		const toolChoices = [{ value: "", label: "Any tool" }, ...catalogue.map(tool => ({ value: tool.name, label: tool.label }))];
		const isNarrowed = search.trim().length > 0 || kind !== "all" || !!toolName;

		return (
			<PlaybookCategoryView
				category={category}
				onBack={this.onBackToCategories}
				action={
					<button type="button" className="tk-button tk-button--primary" onClick={onNewSkill} disabled={isStartingSkill}>
						<Icon.Plus size={14} />
						{isStartingSkill ? "Opening…" : "New skill"}
					</button>
				}
				singular="skill"
				totalCount={categorySkills.length}
				shownCount={shownSkills.length}
				search={search}
				onSearch={({ search: value }) => this.update({ search: value })}
				kind={kind}
				onKind={({ kind: value }) => this.update({ kind: value })}
				filters={
					<ToolsSelect
						filter
						searchable
						searchPlaceholder="Search tools"
						label="Tool"
						value={toolName}
						options={toolChoices}
						onChange={value => this.update({ toolName: value })}
					/>
				}
				isNarrowed={isNarrowed}
				emptyText={
					<React.Fragment>
						No skills in {category.name} yet. Choose <strong>New skill</strong> to write the first one.
					</React.Fragment>
				}
				onClearNarrowing={this.onClearNarrowing}
			>
				{this.renderTable({ shownSkills })}
			</PlaybookCategoryView>
		);
	}

	render() {
		const { categories, isLoadingCategories, categoriesError, drafts, selectedCategoryId, onOpenDraft } = this.props;
		const { isLoading, loadError } = this.state;
		if (isLoading || isLoadingCategories) return <div className="tk-empty">Loading skills…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;
		if (categoriesError) return <div className="tk-notice tk-notice--danger">{categoriesError}</div>;
		const selectedCategory = categories.find(category => Number(category.id) === Number(selectedCategoryId));
		if (selectedCategory) return <div className="agent-skill-list">{this.renderCategory({ category: selectedCategory })}</div>;

		return (
			<div className="agent-skill-list">
				<p className="tk-page-intro">
					The know-how a playbook follows, by the kind of work it serves. Each skill is a procedure in the firm's words and brings the tools it needs;
					the guide adds them to playbooks as it builds.
				</p>
				<BuildDrafts drafts={drafts} describeDraft={({ draft }) => (draft.hasDraftSkill ? "drafted" : "in conversation")} onOpen={onOpenDraft} />
				{this.renderCategories()}
			</div>
		);
	}
}

export default AgentSkillList;
